import { useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import api from '../../api/auth.api';

// Pages that already log their own visit (AdminDashboard, FleetView,
// TrucksLowboys, Bidding). The central tracker skips these so nothing
// double-counts; their hand-wired calls keep their existing behavior
// (e.g. Bidding logs non-admin estimators too).
const SELF_LOGGING = new Set(['dashboard', 'fleet', 'trucks', 'bid-room']);

// A few PWA routes use a different URL than the DASHBOARD_VISITS page key
// bfav5 established for the same concept — map them so both apps aggregate
// under one key. Everything else logs its first path segment as-is.
const PAGE_KEY_OVERRIDES: Record<string, string> = {
  '': 'dashboard',
  'truck-fleet': 'trucks',
  'bidding': 'bid-room',
};

/**
 * Central page-visit logger for the PWA, mirroring bfav5's AdminLayout
 * tracker: one route-change listener instead of hand-wiring each page.
 * Admin users only — the PWA is also the field workers' app, and logging
 * every clock-in open would just be noise in the activity view.
 * Fire-and-forget; never interrupts the user. Renders nothing.
 */
export const PageVisitTracker: React.FC = () => {
  const auth = useContext(AuthContext);
  const location = useLocation();
  const isAdmin = auth?.user?.role === 'admin';
  const segment = location.pathname.split('/').filter(Boolean)[0] || '';
  const page = PAGE_KEY_OVERRIDES[segment] ?? segment;

  useEffect(() => {
    if (!isAdmin || SELF_LOGGING.has(page)) return;
    api.post('/api/admin/dashboard/visit', { page, source: 'pwa' }).catch(() => {});
  }, [isAdmin, page]);

  return null;
};
